/**
 * Workstation Preference Service
 *
 * Manages per-PC (per-browser) workstation and device selections stored in localStorage.
 * Each lab PC selects which workstation it belongs to and which devices to use per type.
 *
 * Keys:
 *   bridge-workstation-id    — selected workstation UUID
 *   bridge-workstation-name  — selected workstation display name
 *   bridge-device-{type}-id   — device UUID for a given device type
 *   bridge-device-{type}-name — device display name for a given device type
 */

export type BridgeDeviceType =
  | "slide_printer"
  | "cassette_printer"
  | "sticker_printer"
  | "document_scanner";

export type PreferredPrinterType = Extract<
  BridgeDeviceType,
  "slide_printer" | "cassette_printer"
>;

export interface PreferredPrinterPreference {
  deviceId: string;
  deviceName: string;
  deviceType: PreferredPrinterType;
  workstationId: string;
  workstationName: string;
}

const WS_ID_KEY = "bridge-workstation-id";
const WS_NAME_KEY = "bridge-workstation-name";

function deviceIdKey(type: BridgeDeviceType): string {
  return `bridge-device-${type}-id`;
}

function deviceNameKey(type: BridgeDeviceType): string {
  return `bridge-device-${type}-name`;
}

function preferredPrinterKey(type: PreferredPrinterType): string {
  return `bridge-preferred-${type}`;
}

function isPreferredPrinterType(value: unknown): value is PreferredPrinterType {
  return value === "slide_printer" || value === "cassette_printer";
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isPreferredPrinterPreference(
  value: unknown,
  expectedType: PreferredPrinterType
): value is PreferredPrinterPreference {
  if (!value || typeof value !== "object") return false;
  const preference = value as Record<string, unknown>;
  return (
    preference.deviceType === expectedType &&
    isNonEmptyString(preference.deviceId) &&
    isNonEmptyString(preference.deviceName) &&
    isNonEmptyString(preference.workstationId) &&
    isNonEmptyString(preference.workstationName)
  );
}

type ChangeHandler = () => void;

class WorkstationPreferenceService {
  private changeListeners: Set<ChangeHandler> = new Set();

  // ── Workstation ──────────────────────────────────────────────

  // eslint-disable-next-line class-methods-use-this
  getSelectedWorkstationId(): string | null {
    try {
      return localStorage.getItem(WS_ID_KEY);
    } catch {
      return null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getSelectedWorkstationName(): string | null {
    try {
      return localStorage.getItem(WS_NAME_KEY);
    } catch {
      return null;
    }
  }

  setSelectedWorkstation(id: string, name: string): void {
    try {
      localStorage.setItem(WS_ID_KEY, id);
      localStorage.setItem(WS_NAME_KEY, name);
    } catch {
      // localStorage unavailable
    }
    this.notifyChange();
  }

  clearSelectedWorkstation(): void {
    try {
      localStorage.removeItem(WS_ID_KEY);
      localStorage.removeItem(WS_NAME_KEY);
    } catch {
      // ignore
    }
    this.notifyChange();
  }

  // ── Devices per type ─────────────────────────────────────────

  // eslint-disable-next-line class-methods-use-this
  getSelectedDeviceId(type: BridgeDeviceType): string | null {
    try {
      return localStorage.getItem(deviceIdKey(type));
    } catch {
      return null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getSelectedDeviceName(type: BridgeDeviceType): string | null {
    try {
      return localStorage.getItem(deviceNameKey(type));
    } catch {
      return null;
    }
  }

  setDeviceForType(
    type: BridgeDeviceType,
    deviceId: string,
    deviceName: string
  ): void {
    try {
      localStorage.setItem(deviceIdKey(type), deviceId);
      localStorage.setItem(deviceNameKey(type), deviceName);
    } catch {
      // ignore
    }
    this.notifyChange();
  }

  clearDeviceForType(type: BridgeDeviceType): void {
    try {
      localStorage.removeItem(deviceIdKey(type));
      localStorage.removeItem(deviceNameKey(type));
    } catch {
      // ignore
    }
    this.notifyChange();
  }

  // ── Optional printer overrides ───────────────────────────────

  // eslint-disable-next-line class-methods-use-this
  getPreferredPrinter(
    type: PreferredPrinterType
  ): PreferredPrinterPreference | null {
    try {
      const storedValue = localStorage.getItem(preferredPrinterKey(type));
      if (!storedValue) return null;
      const preference: unknown = JSON.parse(storedValue);
      if (isPreferredPrinterPreference(preference, type)) return preference;
      return null;
    } catch {
      return null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  hasInvalidPreferredPrinter(type: PreferredPrinterType): boolean {
    try {
      const storedValue = localStorage.getItem(preferredPrinterKey(type));
      if (!storedValue) return false;
      const preference: unknown = JSON.parse(storedValue);
      return !isPreferredPrinterPreference(preference, type);
    } catch {
      return true;
    }
  }

  setPreferredPrinter(preference: PreferredPrinterPreference): void {
    if (
      !isPreferredPrinterType(preference.deviceType) ||
      !isPreferredPrinterPreference(preference, preference.deviceType)
    ) {
      throw new Error("Preferred printer details are invalid");
    }
    localStorage.setItem(
      preferredPrinterKey(preference.deviceType),
      JSON.stringify(preference)
    );
    this.notifyChange();
  }

  clearPreferredPrinter(type: PreferredPrinterType): void {
    localStorage.removeItem(preferredPrinterKey(type));
    this.notifyChange();
  }

  shouldUseBackendForDevice(
    type: PreferredPrinterType,
    deviceId: string
  ): boolean {
    const preference = this.getPreferredPrinter(type);
    const selectedWorkstationId = this.getSelectedWorkstationId();
    const selectedDeviceId = this.getSelectedDeviceId(type);
    if (!selectedWorkstationId) return true;
    if (preference) {
      return (
        preference.deviceId !== deviceId ||
        preference.workstationId !== selectedWorkstationId
      );
    }
    return selectedDeviceId !== deviceId;
  }

  // ── Clear everything ─────────────────────────────────────────

  clearAll(): void {
    this.clearSelectedWorkstation();
    const types: BridgeDeviceType[] = [
      "slide_printer",
      "cassette_printer",
      "sticker_printer",
      "document_scanner",
    ];
    types.forEach((t) => this.clearDeviceForType(t));
  }

  // ── Change listeners ─────────────────────────────────────────

  onChange(handler: ChangeHandler): () => void {
    this.changeListeners.add(handler);
    return () => this.changeListeners.delete(handler);
  }

  private notifyChange(): void {
    this.changeListeners.forEach((handler) => handler());
  }
}

export const workstationPreferenceService = new WorkstationPreferenceService();
